package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.animation
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.bottom
import com.varabyte.kobweb.compose.ui.modifiers.content
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.left
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.translateX
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.css.AnimationTimingFunction
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

enum class LSkeletonShape {
    TEXT,
    RECTANGLE,
    CIRCLE,
}

val LSkeletonShimmer = Keyframes {
    from { Modifier.translateX((-100).percent) }
    to { Modifier.translateX(100.percent) }
}

val LSkeletonStyle = CssStyle {
    base {
        Modifier
            .position(Position.Relative)
            .overflow(Overflow.Hidden)
            // `$p-paper-2` — the sunken well, not `surfaceRaised`. In Day `surfaceRaised` is pure
            // `#FFFFFF`, which is the token reserved for things that genuinely float, so a
            // placeholder for content that has not arrived was painted *above* the card holding it.
            // A skeleton is an absence; an absence is recessed.
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .borderRadius(Radius.Control)
    }
    /**
     * The sheen, and it no longer starts itself.
     *
     * **This ran on an infinite loop on every skeleton in the product**, including the three usage
     * routes whose spec says in as many words that nothing on them animates and that skeletons are
     * "static `$p-paper-2` blocks — no shimmer, no pulse". It also broke the motion system's own
     * rule that nothing animates without being asked to: a loading state is the one moment a page
     * has nothing to say, and a permanent 1.2s loop there is movement the reader did not cause and
     * cannot stop.
     *
     * Gated on an attribute rather than deleted, and gated inside this style rather than split into
     * a second one, for the reason `LButtonStyle`'s hit-area rule records: a new top-level
     * `CssStyle` has to be registered by hand in every test harness that renders it, and the
     * failure when it is not is an error that points at the component instead of at the list.
     */
    cssRule("[data-shimmer]::after") {
        val sheen = colorMode.toSitePalette().textMuted.toRgb().copyf(alpha = 0.14f)
        Modifier
            .content("")
            .position(Position.Absolute)
            .top(0.px)
            .right(0.px)
            .bottom(0.px)
            .left(0.px)
            .styleModifier {
                property("background", "linear-gradient(90deg, transparent, $sheen, transparent)")
            }
            // **Not a `Motion` duration, and deliberately not on that scale.** Those price discrete
            // transitions and entrances, where the curve carries meaning and 1200ms would read as
            // a stall. This is a continuous loop: `Linear` is correct precisely because a shimmer
            // that eased would pulse, and 1200ms is its period rather than how long anything takes.
            // A token for it would have exactly one caller and no drift to prevent.
            .animation(
                LSkeletonShimmer.toAnimation(
                    colorMode = colorMode,
                    duration = 1200.ms,
                    timingFunction = AnimationTimingFunction.Linear,
                    iterationCount = AnimationIterationCount.Infinite,
                ),
            )
    }
}

/**
 * A block where content will be.
 *
 * [shimmer] is off unless a surface asks for it, which is the reverse of what shipped. See the
 * `[data-shimmer]::after` rule above for the two rules a self-starting loop broke; the short
 * version is that a skeleton is a statement about absence, and absence does not move.
 */
@Composable
fun LSkeleton(
    modifier: Modifier = Modifier,
    width: CSSLengthOrPercentageNumericValue = 100.percent,
    height: CSSLengthOrPercentageNumericValue = 20.px,
    shape: LSkeletonShape = LSkeletonShape.RECTANGLE,
    /**
     * What one bar announces, or `null` to make it decorative.
     *
     * A single skeleton standing for a value is worth announcing. A stack of them is not: each one
     * is its own `status` region, so eight bars are eight simultaneous announcements of the same
     * sentence. Pass `null` for every bar in a group and let one region speak for all of them —
     * [LoadingState] does exactly that.
     */
    label: String? = t("ds.ds.lskeleton.loading_content", "Loading content"),
    shimmer: Boolean = false,
) {
    val shapeModifier = when (shape) {
        LSkeletonShape.TEXT -> Modifier.borderRadius(Radius.Control)
        LSkeletonShape.RECTANGLE -> Modifier.borderRadius(Radius.Card)
        LSkeletonShape.CIRCLE -> Modifier.borderRadius(Radius.Pill)
    }
    Div(
        LSkeletonStyle.toModifier()
            .width(width)
            .height(height)
            .then(shapeModifier)
            .then(
                if (label == null) {
                    Modifier.attr("aria-hidden", "true")
                } else {
                    Modifier.role("status").ariaLabel(label)
                },
            )
            .then(modifier)
            .toAttrs {
                if (shimmer) attr("data-shimmer", "")
            },
    )
}
